// 표시 형식의 **보조 자료 출처 선언**(`BindingFormat.scopeFrom`) 저작 로직 — 순수 함수 층.
//
// 형식의 여러 kind가 값 하나로는 표시를 만들지 못하고 스코프에서 보조 데이터를 읽는다
// (unit.unitPath / template·switch의 `{a.b}` / switch.on / rangeMap.rangesKey·annotationKey).
// 그 조회는 전부 **경로 문자열**이라, 그 이름이 스코프에 어떻게 실리느냐가 형식 밖의 관례였다.
// `scopeFrom`은 그 연결을 형식 자신에게 준다 — 카탈로그 entry를 지정한 이름으로 스코프에 얹으면
// 경로 표현식은 한 글자도 고치지 않고 그대로 산다.
// 결정 기록: docs/decisions/2026-08-07-format-scope-declaration-axis.md
//
// 여기엔 화면이 없다 — 행 추가/삭제/패치, 저장 형상 정리(빈 배열이면 키 제거), 노출 게이트,
// 저작자에게 보일 경고 문구 판정만 담는다. 렌더는 EditorPage의 FormatScopeFromEditor.

import type { BindingFormat, FormatScopeSource } from '@safetyhub/shared';

/**
 * 이 kind가 **실제로 스코프를 읽는가** — 안 먹는 자리에 컨트롤을 띄우지 않기 위한 게이트.
 *
 * 판정 근거는 rc `applyFormat`의 각 case가 `scope`를 만지는지다(추정 아님):
 *  - unit     : `getByPath(scope, unitPath)`
 *  - template : `applyTemplate(value, template, scope)` — `{a.b}` placeholder
 *  - switch   : `getByPath(scope, on)` + 선택된 케이스 템플릿의 placeholder
 *  - rangeMap : rangesKey(구간 배열 경로) · annotationKey(병기 값) — 둘 다 스코프 경로
 * 나머지는 값만 본다:
 *  - date·default·map·bool : 스코프 조회 없음
 *  - join     : itemTemplate의 스코프는 **바깥 스코프가 아니라 항목 자신**(`applyTemplate(item, tpl, item)`)
 *  - url      : `scope.__fileHost` 하나만 읽는데 그건 호스트가 주입하는 값이라 저작 대상이 아니다
 */
export function formatReadsScope(kind: BindingFormat['kind']): boolean {
  switch (kind) {
    case 'unit':
    case 'template':
    case 'switch':
    case 'rangeMap':
      return true;
    case 'date':
    case 'url':
    case 'default':
    case 'join':
    case 'map':
    case 'bool':
      return false;
    default: {
      // 새 kind가 생기면 여기서 tsc가 막는다 — "스코프를 읽는가"는 kind마다 답이 달라 기본값이 없다.
      const exhaustive: never = kind;
      return exhaustive;
    }
  }
}

/** 형식에 선언된 출처 목록(없으면 빈 배열). 배열이 아닌 값이 들어와도 빈 배열로 degrade. */
export function scopeSourcesOf(format: BindingFormat | undefined): FormatScopeSource[] {
  const raw = format?.scopeFrom;
  return Array.isArray(raw) ? raw : [];
}

/**
 * 편집기를 띄울 것인가.
 *
 * 스코프를 읽는 kind면 당연히 띄우고, **읽지 않는 kind라도 선언이 이미 들어 있으면 띄운다** —
 * 게이트로 가려 버리면 저작자에게는 "저작한 게 사라진" 것이고, 지울 수단도 함께 사라진다
 * (rangeMap 고급 영역이 값 있으면 펼쳐 두는 것과 같은 원칙).
 */
export function shouldShowScopeEditor(format: BindingFormat | undefined): boolean {
  if (!format) return false;
  return formatReadsScope(format.kind) || scopeSourcesOf(format).length > 0;
}

/** 이 행이 실제로 적용되는가 — rc `withFormatScope`의 채택 조건(이름·출처 둘 다 비지 않음)과 같다. */
export function isScopeSourceUsable(source: FormatScopeSource | undefined): boolean {
  if (!source) return false;
  return (
    typeof source.key === 'string' &&
    source.key !== '' &&
    typeof source.catalogKey === 'string' &&
    source.catalogKey !== ''
  );
}

/**
 * 행 하나의 문제를 저작자 문장으로. 없으면 undefined.
 *
 * 미완성 행을 **지우지 않고** 경고만 하는 이유: 이름을 지우는 순간 행이 사라지면 편집이 불가능하다
 * (rc는 미완성 행을 그냥 건너뛰므로 저장돼 있어도 표시가 깨지지 않는다).
 * `duplicateKeys`를 주면 같은 이름이 둘 이상인 것도 잡는다 — 뒤 선언이 앞 선언을 덮어써
 * 앞의 것이 조용히 무효가 되기 때문이다(rc는 목록 순서대로 얹는다).
 */
export function scopeSourceIssue(
  source: FormatScopeSource,
  duplicateKeys?: ReadonlySet<string>,
): string | undefined {
  const key = source.key ?? '';
  if (key === '') return '이름을 적어야 적용됩니다.';
  // 이름은 경로 표현식의 **첫 마디**로 읽힌다 — 마침표가 들어가면 그 지점에서 경로가 갈라져
  // 형식이 영영 찾지 못하는 이름이 된다(조용한 미적용).
  if (key.includes('.')) return '이름에 마침표(.)를 쓸 수 없습니다 — 경로를 나누는 기호입니다.';
  if ((source.catalogKey ?? '') === '') return '가져올 데이터를 골라야 적용됩니다.';
  if (duplicateKeys?.has(key)) return '같은 이름이 둘 이상입니다 — 아래쪽 선언만 적용됩니다.';
  return undefined;
}

/** 목록에서 두 번 이상 나오는 이름(빈 이름 제외). */
export function duplicateScopeKeys(sources: readonly FormatScopeSource[]): Set<string> {
  const seen = new Set<string>();
  const dupes = new Set<string>();
  for (const source of sources) {
    const key = source?.key ?? '';
    if (key === '') continue;
    if (seen.has(key)) dupes.add(key);
    seen.add(key);
  }
  return dupes;
}

/**
 * 빈 문자열 select는 "경로 없음"(카탈로그 값 자체)이라 키를 남기지 않는다 — 저장 형상 정리.
 * 필드를 골라 옮기지 않고 **스프레드로 보존**한다 — 나중에 선언에 필드가 늘었을 때 편집 한 번으로
 * 그 필드가 조용히 사라지지 않게(silent drop 방지).
 */
function normalizeScopeSource(source: FormatScopeSource): FormatScopeSource {
  const next: FormatScopeSource = {
    ...source,
    key: source.key ?? '',
    catalogKey: source.catalogKey ?? '',
  };
  if (typeof next.select !== 'string' || next.select === '') delete next.select;
  return next;
}

/**
 * 목록을 형식에 반영한다.
 *
 * **빈 목록이면 `scopeFrom` 키 자체를 지운다** — `scopeFrom: []`를 저장하면 선언한 적 없는 형식에도
 * 키가 늘어 템플릿 JSON이 오염되고, "선언이 없으면 종전과 완전히 동일"(회귀 0)이라는 축의 약속이
 * 저장 형상에서 깨진다.
 */
export function withScopeSources(
  format: BindingFormat,
  sources: readonly FormatScopeSource[],
): BindingFormat {
  // BindingFormat은 인라인 discriminated union이라 스프레드 결과에 옵셔널 키를 넣고 빼는 조작을
  // 타입으로 표현할 수 없다(kind별 멤버가 모두 다르다). 키 조작만 Record로 하고 형태는 보존한다.
  const next: Record<string, unknown> = { ...(format as unknown as Record<string, unknown>) };
  if (sources.length === 0) delete next.scopeFrom;
  else next.scopeFrom = sources.map(normalizeScopeSource);
  return next as unknown as BindingFormat;
}

/** 빈 행 하나 추가(이름·출처는 저작자가 채운다 — 임의로 지어내지 않는다). */
export function addScopeSource(format: BindingFormat): BindingFormat {
  return withScopeSources(format, [...scopeSourcesOf(format), { key: '', catalogKey: '' }]);
}

/** index 행 삭제. 마지막 한 줄을 지우면 `scopeFrom` 키까지 사라진다(withScopeSources 규약). */
export function removeScopeSourceAt(format: BindingFormat, index: number): BindingFormat {
  return withScopeSources(
    format,
    scopeSourcesOf(format).filter((_, i) => i !== index),
  );
}

/**
 * index 행의 일부 필드를 고친다. 범위 밖 index면 원본 그대로.
 * `select`는 빈 문자열·undefined **둘 다 해제**로 본다(normalizeScopeSource).
 */
export function patchScopeSourceAt(
  format: BindingFormat,
  index: number,
  patch: Partial<FormatScopeSource>,
): BindingFormat {
  const sources = scopeSourcesOf(format);
  if (index < 0 || index >= sources.length) return format;
  return withScopeSources(
    format,
    sources.map((source, i) => (i === index ? { ...source, ...patch } : source)),
  );
}

/**
 * 출처 picker(카탈로그 + 경로 한 벌)의 변경을 index 행에 반영한다.
 * picker가 해제(undefined)를 내면 **이름은 남기고 출처만 비운다** — 이름은 저작자가 친 것이라,
 * 출처를 바꾸려고 ✕를 눌렀을 뿐인데 이름까지 날아가면 다시 쳐야 한다.
 */
export function setScopeSourceOrigin(
  format: BindingFormat,
  index: number,
  origin: { catalogKey: string; select?: string } | undefined,
): BindingFormat {
  return patchScopeSourceAt(format, index, {
    catalogKey: origin?.catalogKey ?? '',
    select: origin?.select,
  });
}

/**
 * kind 전환 시 이어받을 선언 — **새 kind도 스코프를 읽을 때만** 이어받는다(안 읽는 kind로 옮기면
 * 죽은 선언이 저장에 남는다). `empty` 이어받기(formatEmptyOf)와 같은 취지다: kind를 바꿔 볼 때마다
 * 저작한 것이 조용히 사라지지 않게.
 */
export function carryScopeSources(
  prev: BindingFormat | undefined,
  nextKind: BindingFormat['kind'] | 'none',
): FormatScopeSource[] | undefined {
  if (nextKind === 'none') return undefined;
  if (!formatReadsScope(nextKind)) return undefined;
  const sources = scopeSourcesOf(prev);
  return sources.length > 0 ? sources : undefined;
}

/** kind별로 "이 선언이 어디에 쓰이는지" 한 줄 — 저작자가 이름을 어디에 적을지 알 수 있게. */
export function scopeUsageHint(kind: BindingFormat['kind']): string {
  switch (kind) {
    case 'unit':
      return 'unitPath에 적은 경로가 이 이름들을 찾습니다.';
    case 'template':
      return '문구의 {이름.항목} 자리가 이 이름들을 찾습니다.';
    case 'switch':
      return 'on 경로와 케이스 문구의 {이름.항목} 자리가 이 이름들을 찾습니다.';
    case 'rangeMap':
      return 'rangesKey·병기 값 경로가 이 이름들을 찾습니다.';
    default:
      // 스코프를 읽지 않는 kind에 남아 있는 선언(이전 kind에서 넘어온 것) — 지울 수 있도록 편집기는
      // 보이되, 지금은 아무 데도 쓰이지 않는다는 사실을 분명히 말한다.
      return '이 형식은 보조 자료를 읽지 않아 선언이 적용되지 않습니다.';
  }
}
